import React from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { WeatherData } from './flightDetails.types';

const formatWindDirection = (deg: number | null): string => {
  if (deg === null || deg === undefined) return 'N/A';
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return directions[Math.round(deg / 45) % 8];
};

interface FlightWeatherSectionProps {
  weather: WeatherData | null;
  isFetching: boolean;
  /** Absent on a flight another organization shared: fetching stores weather on its row (#1693). */
  onFetch?: () => void;
  labelStyle: Record<string, unknown>;
}

export const FlightWeatherSection: React.FC<FlightWeatherSectionProps> = ({
  weather,
  isFetching,
  onFetch,
  labelStyle,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" sx={{ ...labelStyle, display: 'block', mb: 1 }}>
      Weather Conditions
    </Typography>
    {weather ? (
      <>
        <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={labelStyle}>
              Temperature
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {weather.temperature_c !== null ? `${weather.temperature_c.toFixed(1)}°C` : 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={labelStyle}>
              Humidity
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {weather.humidity_percent !== null ? `${weather.humidity_percent}%` : 'N/A'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={labelStyle}>
              Wind
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {weather.wind_speed_ms !== null
                ? `${weather.wind_speed_ms.toFixed(1)} m/s ${formatWindDirection(
                    weather.wind_direction_deg
                  )}`
                : 'N/A'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={labelStyle}>
              Precipitation
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {weather.precipitation_mm !== null
                ? weather.precipitation_mm > 0
                  ? `${weather.precipitation_mm.toFixed(1)} mm`
                  : 'None'
                : 'N/A'}
            </Typography>
          </Box>
        </Box>
      </>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No weather data available
        </Typography>
        {onFetch && (
          <Button
            size="small"
            variant="outlined"
            onClick={onFetch}
            disabled={isFetching}
            sx={{
              ml: 1,
              borderColor: BRAND_COLORS.forestGreen.main,
              color: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                borderColor: BRAND_COLORS.forestGreen.main,
                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.04),
              },
            }}
          >
            {isFetching ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            {isFetching ? 'Fetching...' : 'Fetch Weather'}
          </Button>
        )}
      </Box>
    )}
  </Box>
);
